export enum Env {
  LOCAL = "local",
  DEV = "dev",
  PROD = "prod",
}

export enum Brand {
  FINN = "FINN",
  TORI = "TORI",
}

interface ApiHosts {
  [Env.LOCAL]: string;
  [Env.DEV]: string;
  [Env.PROD]: string;
}

interface RestConfig {
  baseUrl: string;
  credentials: RequestCredentials;
}

const proxyUrl =
  "saved-search-settings-proxy/podium-resource/savedSearchSettings";

const productionHosts = [
  "www.finn.no",
  "finn.no",
  "www.beta.tori.fi",
  "beta.tori.fi",
  "www.tori.fi",
  "tori.fi",
];

const Hosts: Record<Brand, ApiHosts> = {
  [Brand.FINN]: {
    [Env.LOCAL]: "http://local.finn.no:8086",
    [Env.DEV]: "https://dev.finn.no",
    [Env.PROD]: "https://www.finn.no",
  },
  [Brand.TORI]: {
    [Env.LOCAL]: "http://local.tori.fi:8086",
    [Env.DEV]: "https://toripg.rc.nmp.finn.no",
    [Env.PROD]: "https://www.tori.fi",
  },
} as const;

export class ApplicationConfig {
  readonly env: Env;

  readonly brand: Brand;

  constructor() {
    this.env = ApplicationConfig.resolveEnv();
    this.brand = ApplicationConfig.resolveBrand();
  }

  public getRestConfig(): RestConfig {
    const apiHost = Hosts[this.brand][this.env];
    const baseUrl = `${apiHost}/${proxyUrl}`;
    const credentials = this.env === Env.LOCAL ? "include" : "same-origin";

    return { baseUrl, credentials };
  }

  private static resolveEnv(): Env {
    if (this.isServer()) {
      switch (process.env.FIAAS_ENVIRONMENT) {
        case Env.LOCAL:
          return Env.LOCAL;
        case Env.DEV:
          return Env.DEV;
        case Env.PROD:
          return Env.PROD;
        default:
          console.warn("Could not resolve env, falling back to default.");
          return Env.DEV;
      }
    }

    const { host } = window.location;
    if (host.includes("local.") || host.includes("localhost")) return Env.LOCAL;
    if (host.includes("dev.") || host.includes("toripg.rc.nmp")) return Env.DEV;
    if (productionHosts.includes(host)) return Env.PROD;

    console.warn("Could not resolve env, falling back to default.");
    return Env.DEV;
  }

  private static resolveBrand(): Brand {
    if (this.isServer()) {
      switch (process.env.NMP_BRAND) {
        case Brand.TORI:
          return Brand.TORI;
        case Brand.FINN:
          return Brand.FINN;
        default:
          return Brand.FINN;
      }
    }

    const { host } = window.location;
    if (host.includes("tori.fi") || host.includes("toripg.rc.nmp"))
      return Brand.TORI;
    if (host.includes("finn.no")) return Brand.FINN;
    return Brand.FINN;
  }

  private static isServer(): boolean {
    return typeof window === "undefined";
  }
}
